import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ChevronUp, Bug, ArrowLeft, Home, Lock, User, Lightbulb, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { bugAPI, featureAPI, BugSubmissionRequest, BugResponse, ApiError } from "../services/api";
import PageWithNavbar from "@/components/PageWithNavbar";

export const BugTrackerPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [bugs, setBugs] = useState<BugResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [newBug, setNewBug] = useState({
    title: "",
    description: "",
  });

  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<"bug" | "feature">("bug");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load bugs and features on component mount
  useEffect(() => {
    const loadIssues = async () => {
      try {
        setIsLoading(true);
        const [bugsResponse, featuresResponse] = await Promise.all([
          bugAPI.getBugs(),
          featureAPI.getFeatures(),
        ]);
        
        const allIssues = [
          ...(bugsResponse || []),
          ...(featuresResponse || []),
        ];
        
        setBugs(allIssues);
      } catch (error) {
        const errorMessage = error instanceof ApiError 
          ? error.message 
          : 'Failed to load issues. Please try again.';
        setError(errorMessage);
        console.error('Error loading issues:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadIssues();
  }, []);

  const handleUpvote = async (bugId: number) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    
    try {
      const bug = bugs.find(b => b.id === bugId);
      if (!bug) return;

      const voteAPI = bug.type === "bug" ? bugAPI.voteBug : featureAPI.voteFeature;
      const response = await voteAPI(bugId);
      
      if (response) {
        setBugs(bugs.map(b => 
          b.id === bugId 
            ? { 
                ...b, 
                votes: response.votes,
                hasVoted: response.hasVoted 
              }
            : b
        ));
      }
    } catch (error) {
      console.error('Error voting:', error);
      // Optionally show error message to user
    }
  };

  const handleSubmitBug = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user is authenticated
    if (!isAuthenticated || !user) {
      navigate("/login");
      return;
    }

    if (!newBug.title || !newBug.description) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const submissionData: BugSubmissionRequest = {
        title: newBug.title,
        description: newBug.description,
        type: formType,
      };

      const submitAPI = formType === "bug" ? bugAPI.submitBug : featureAPI.submitFeature;
      const response = await submitAPI(submissionData);
      
      if (response) {
        // Add the new bug/feature to the list
        setBugs([response, ...bugs]);
        
        // Reset form
        setNewBug({ title: "", description: "" });
        setShowForm(false);
        setFormType("bug"); // Reset to default
      } else {
        setError(response || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      const errorMessage = error instanceof ApiError 
        ? error.message 
        : 'Network error. Please try again.';
      setError(errorMessage);
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open": return "destructive";
      case "in-progress": return "default";
      case "testing": return "secondary";
      case "fixed": return "outline";
      default: return "outline";
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setNewBug(prev => ({ ...prev, [field]: value }));
    // Clear global error when user starts typing
    if (error) {
      setError(null);
    }
  };

  // Filter bugs by status
  const openBugs = bugs.filter(bug => bug.status === "open" || bug.status === "in-progress");
  const testingBugs = bugs.filter(bug => bug.status === "testing");
  const fixedBugs = bugs.filter(bug => bug.status === "fixed");

  // Sort each category by votes
  const sortedOpenBugs = [...openBugs].sort((a, b) => {
    if (b.votes === a.votes) {
      // If votes are equal, sort by date (newer first)
      return new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime();
    }
    return b.votes - a.votes;
  });
  const sortedTestingBugs = [...testingBugs].sort((a, b) => b.votes - a.votes);
  const sortedFixedBugs = [...fixedBugs].sort((a, b) => b.votes - a.votes);

  const renderBugList = (bugList: BugResponse[], title: string, description: string) => (
    <div className="mb-12">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2">{title}</h2>
        <p className="text-muted-foreground">{description}</p>
      </div>
      <div className="max-w-4xl mx-auto space-y-6">
        {bugList.map((bug) => (
          <Card key={bug.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      {bug.type === "bug" ? (
                        <Bug className="h-4 w-4 text-red-500" />
                      ) : (
                        <Lightbulb className="h-4 w-4 text-blue-500" />
                      )}
                      <Badge variant={bug.type === "bug" ? "destructive" : "default"} className="text-xs">
                        {bug.type === "bug" ? "Bug" : "Feature"}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg">{bug.title}</CardTitle>
                    <Badge variant={getStatusColor(bug.status)}>
                      {bug.status.replace("-", " ")}
                    </Badge>
                  </div>
                  <CardDescription className="text-sm">
                    Submitted by {bug.submittedBy} on {bug.submittedDate}
                  </CardDescription>
                </div>
                <div className="flex flex-col items-center ml-4">
                  <Button
                    variant={bug.hasVoted ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleUpvote(bug.id)}
                    className="flex flex-col h-auto py-2 px-3"
                  >
                    <ChevronUp className="h-4 w-4" />
                    <span className="text-xs font-bold">{bug.votes}</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">{bug.description}</p>
            </CardContent>
          </Card>
        ))}
        {bugList.length === 0 && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No bugs in this category yet.</p>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading issues...</p>
        </div>
      </div>
    );
  }

  return (
    <PageWithNavbar>
    <div className="container mx-auto px-4 py-16">
      {/* Navigation */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Button 
            onClick={() => navigate("/roadmap")} 
            variant="ghost" 
            className="justify-start"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Roadmap
          </Button>
          <Button 
            onClick={() => navigate("/")} 
            variant="ghost" 
            className="justify-start"
          >
            <Home className="mr-2 h-4 w-4" />
            Home
          </Button>
        </div>
      </div>

      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Bug className="h-12 w-12 text-primary mr-3" />
          <h1 className="text-4xl font-bold">Bug Tracker</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Help us improve by reporting bugs and voting on issues that matter most to you.
        </p>
        {!isAuthenticated && (
          <div className="mt-4 p-4 bg-muted/50 rounded-lg max-w-md mx-auto">
            <div className="flex items-center gap-2 justify-center text-muted-foreground mb-2">
              <Lock className="h-4 w-4" />
              <span className="text-sm font-medium">Login Required for Bug Reporting</span>
            </div>
            <p className="text-sm text-muted-foreground">
              You can view existing bugs and their status, but you'll need to{" "}
              <button
                onClick={() => navigate("/login")}
                className="text-primary hover:underline font-medium"
              >
                sign in
              </button>{" "}
              to report new bugs.
            </p>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-100 border border-red-300 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Open and In-Progress Bugs */}
      {renderBugList(sortedOpenBugs, "Open & In-Progress Issues", "Bugs that need attention and are currently being worked on.")}

      {/* Testing Bugs */}
      {renderBugList(sortedTestingBugs, "Bugs in Testing", "Issues that have been fixed and are currently being tested.")}

      {/* Fixed Bugs */}
      {renderBugList(sortedFixedBugs, "Fixed Issues", "Bugs that have been resolved and deployed.")}

      {/* Submit Bug Form */}
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          {isAuthenticated ? (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => {
                  setFormType("bug");
                  setShowForm(!showForm || formType !== "bug");
                }}
                className="flex items-center gap-2"
                variant={formType === "bug" && showForm ? "default" : "outline"}
                size="lg"
              >
                <Bug className="h-4 w-4" />
                {(showForm && formType === "bug") ? "Hide Form" : "Report a Bug"}
              </Button>
              <Button
                onClick={() => {
                  setFormType("feature");
                  setShowForm(!showForm || formType !== "feature");
                }}
                className="flex items-center gap-2"
                variant={formType === "feature" && showForm ? "default" : "outline"}
                size="lg"
              >
                <Lightbulb className="h-4 w-4" />
                {(showForm && formType === "feature") ? "Hide Form" : "Request Feature"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Lock className="h-5 w-5" />
                <span>Login required to report bugs or request features</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/login")}
                  className="flex items-center gap-2"
                  size="lg"
                  variant="outline"
                >
                  <User className="h-4 w-4" />
                  Sign In to Report Bug
                </Button>
                <Button
                  onClick={() => navigate("/login")}
                  className="flex items-center gap-2"
                  size="lg"
                  variant="outline"
                >
                  <User className="h-4 w-4" />
                  Sign In to Request Feature
                </Button>
              </div>
            </div>
          )}
        </div>

        {showForm && isAuthenticated && (
          <Card>
            <CardHeader>
              <CardTitle>
                {formType === "bug" ? "Submit a Bug Report" : "Request a Feature"}
              </CardTitle>
              <CardDescription>
                {formType === "bug" 
                  ? "Help us fix issues by providing detailed information about the bug you encountered."
                  : "Share your ideas for new features that would improve your trading experience."
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmitBug} className="space-y-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium mb-2">
                    {formType === "bug" ? "Bug Title" : "Feature Title"} *
                  </label>
                  <Input
                    id="title"
                    placeholder={formType === "bug" 
                      ? "Brief description of the issue" 
                      : "Brief description of the feature you'd like"
                    }
                    value={newBug.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium mb-2">
                    Description *
                  </label>
                  <textarea
                    id="description"
                    placeholder={formType === "bug"
                      ? "Detailed description of the bug, steps to reproduce, and expected behavior"
                      : "Detailed description of the feature, how it would work, and why it would be useful"
                    }
                    value={newBug.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    required
                    className="w-full min-h-[100px] px-3 py-2 border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-md"
                  />
                </div>

                <div className="bg-muted/50 p-3 rounded-md">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Submitting as:</span>
                    <span className="text-muted-foreground">({user?.email})</span>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="submit" 
                    className="flex-1"
                    disabled={isSubmitting}
                  >
                    {isSubmitting 
                      ? (formType === "bug" ? "Submitting Bug..." : "Submitting Feature...")
                      : (formType === "bug" ? "Submit Bug Report" : "Submit Feature Request")
                    }
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowForm(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </PageWithNavbar>
  );
};

export default BugTrackerPage;
